var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { resetPasswordApi } from "apis/eduidResetPassword";
import { EmailInput } from "components/Common/EmailInput";
import { ResponseCodeButtons } from "components/Common/ResponseCodeAbortButton";
import { ResponseCodeForm } from "components/Login/ResponseCodeForm";
import { useAppDispatch, useAppSelector } from "eduid-hooks";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { clearNotifications } from "slices/Notifications";
import resetPasswordSlice from "slices/ResetPassword";
import { ResetPasswordStepIndicator } from "./ResetPasswordStepIndicator";
/**
 * Enter the emailed code on a browser that did not start the reset process.
 *
 * The code is resolved from the eppn in the session, so a browser with no identity hint has to
 * send the email address along with the code. This is also where the user is sent when a session
 * turns out not to have completed the code step.
 */
export function ResetPasswordEnterCode() {
    const dispatch = useAppDispatch();
    const email_address = useAppSelector((state) => state.resetPassword.email_address);
    const dashboard_link = useAppSelector((state) => state.config.dashboard_link);
    const [verifyEmailLink] = resetPasswordApi.useLazyVerifyEmailLinkQuery();
    const [emailMissing, setEmailMissing] = useState(false);
    function handleSubmitCode(values) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const email_code = values.v.join("");
            const email = (_a = values.email) !== null && _a !== void 0 ? _a : email_address;
            if (!email) {
                // Reachable, in spite of the Ok button being disabled without an email address:
                // ResponseCodeForm submits on Enter whether the form is valid or not, and EmailInput
                // deliberately does not flag a blank value while autoComplete is set (browsers fill in
                // addresses without telling Javascript). Say what is missing - returning quietly here leaves
                // the user pressing Enter on a filled in code with nothing happening and nothing explaining
                // why.
                setEmailMissing(true);
                return;
            }
            setEmailMissing(false);
            const response = yield verifyEmailLink({ email_code, email });
            if (response.isSuccess) {
                dispatch(clearNotifications());
                if (Object.values(response.data.payload.extra_security).length > 0) {
                    dispatch(resetPasswordSlice.actions.setNextPage("HANDLE_EXTRA_SECURITIES"));
                }
                else {
                    dispatch(resetPasswordSlice.actions.setNextPage("SET_NEW_PASSWORD"));
                }
            }
        });
    }
    function handleAbortButtonOnClick(event) {
        event === null || event === void 0 ? void 0 : event.preventDefault();
        if (dashboard_link) {
            globalThis.location.href = dashboard_link;
        }
    }
    return (_jsxs("div", { className: "step-container", children: [_jsxs("section", { className: "intro", children: [_jsx("h1", { children: _jsx(FormattedMessage, { id: "enterCode.heading", defaultMessage: "Reset Password: Enter email code", description: "Reset Password enter code heading" }) }), _jsx("div", { className: "lead", children: _jsx("p", { children: _jsx(FormattedMessage, { id: "enterCode.lead", defaultMessage: "Enter the email address that the code was sent to, and the code from the email.", description: "Reset Password enter code lead" }) }) })] }), _jsx("div", { className: "enter-code", children: _jsx(ResponseCodeForm, { inputsDisabled: false, autoFocusCode: false, extraFields: _jsxs(React.Fragment, { children: [_jsx(EmailInput, { name: "email", required: true, autoFocus: true, autoComplete: "username", defaultValue: email_address, "aria-invalid": emailMissing, "aria-errormessage": emailMissing ? "missing-email-error" : undefined }), emailMissing && (_jsx("span", { role: "alert", className: "input-validate-error", id: "missing-email-error", children: _jsx(FormattedMessage, { id: "enterCode.error", defaultMessage: "Enter the email address that the code was sent to.", description: "Reset Password enter code missing email error" }) }))] }), handleSubmitCode: handleSubmitCode, children: _jsx(ResponseCodeButtons, { handleAbortButtonOnClick: handleAbortButtonOnClick, requireEmail: true }) }) }), _jsx(ResetPasswordStepIndicator, { currentStep: 3 })] }));
}
